import React, { useState, useEffect } from 'react';
import { Card, Badge } from '../../../components/ui';
import { Store, MapPin, Phone, Users, RefreshCw } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

interface LocalDetalle {
  id: string;
  nombre: string;
  direccion: string | null;
  telefono: string | null;
  activo: boolean;
  vendedores_activos: number;
  vendedores_total: number;
}

export const LocalesPage: React.FC = () => {
  const [local, setLocal] = useState<LocalDetalle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<LocalDetalle>('/usuarios/local/mi-local');
      setLocal(data);
    } catch (e: any) {
      setError(e.message || 'No se pudo cargar la informacion del local');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Local</h1>
          <p className="text-gray-500">Informacion de tu punto de venta</p>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      </div>
    );
  }

  if (!local) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Local</h1>
          <p className="text-gray-500">Informacion de tu punto de venta</p>
        </div>
        <button
          onClick={cargarDatos}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      <Card>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Store className="w-7 h-7 text-primary-600" />
          </div>
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">{local.nombre}</h2>
              <Badge variant={local.activo ? 'success' : 'neutral'}>
                {local.activo ? 'ACTIVO' : 'INACTIVO'}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={16} className="text-gray-400" />
                <span>{local.direccion || 'Sin direccion'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={16} className="text-gray-400" />
                <span>{local.telefono || 'Sin telefono'}</span>
              </div>
            </div>

            <p className="text-xs text-gray-400">ID: {local.id}</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Vendedores activos</p>
              <p className="text-2xl font-bold text-blue-600">{local.vendedores_activos}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Vendedores totales</p>
              <p className="text-2xl font-bold text-gray-700">{local.vendedores_total}</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LocalesPage;
